import React, { useMemo } from 'react';
import { RawTrader, TraderDerivation } from './types';
import {
  rng,
  hash,
  money,
  sMoney,
  sPct,
  monthsCalc,
  POS,
  NEG,
  INK,
} from './constants';

interface AnalyticsTabProps {
  trader: RawTrader;
  derivation: TraderDerivation;
}

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ trader, derivation: d }) => {
  const mv = useMemo(() => monthsCalc(trader), [trader]);
  const winMonths = useMemo(() => mv.filter((v) => v > 0).length, [mv]);

  const symBase = trader.tags.includes('Indices')
    ? ['XAUUSD', 'EURUSD', 'US30', 'GBPUSD']
    : ['XAUUSD', 'EURUSD', 'GBPUSD', 'USDJPY'];
  const shares = [42, 27, 19, 12];

  const r6 = useMemo(() => rng(hash(trader.name) + 81), [trader.name]);
  const distSym = useMemo(() => {
    return symBase.map((sym, i) => {
      const deals = Math.round((d.trades * shares[i]) / 100);
      const buy = 0.35 + r6() * 0.3;
      const gp = ((d.profit * shares[i]) / 100) * (i === 3 ? -0.6 : 1) * (0.7 + r6() * 0.6);
      return {
        sym,
        deals,
        dealW: (shares[i] / 42) * 100 + '%',
        buyW: (buy * 100).toFixed(0) + '%',
        sellW: ((1 - buy) * 100).toFixed(0) + '%',
        gp: (gp >= 0 ? '+' : '-') + '$' + Math.abs(Math.round(gp / 100) / 10).toFixed(1) + 'K',
        gpW: Math.min((Math.abs(gp) / Math.abs(d.profit || 1)) * 180, 100) + '%',
        gpC: gp >= 0 ? POS : NEG,
        gpBg: gp >= 0 ? '#D8EFDF' : '#FADED9',
        gpBd: gp >= 0 ? '#93CFA6' : '#E8A79E',
      };
    });
  }, [symBase, d.trades, d.profit, r6]);

  const distW = [0.03, 0.08, 0.14, 0.13, 0.18, 0.22, 0.15, 0.07];
  const distMax = Math.max(...distW);
  const dist = ['<-2%', '-2..-1', '-1..-.5', '-.5..0', '0..0.5', '0.5..1', '1..2', '>2%'].map((label, i) => ({
    label,
    n: Math.round(d.trades * distW[i]),
    h: Math.round((distW[i] / distMax) * 118) + 'px',
    c: i < 4 ? '#EFB3AE' : '#3DBFBE',
  }));

  const r7 = useMemo(() => rng(hash(trader.name) + 51), [trader.name]);
  const activity = useMemo(() => {
    const actRaw = Array.from({ length: 24 }, () => 0.35 + r7() * 0.65);
    const actMax = Math.max(...actRaw);
    return actRaw.map((v) => ({ h: Math.round((v / actMax) * 100) + 'px', c: v > 0.85 ? '#E39A2B' : '#C9D3DE' }));
  }, [r7]);

  const statL = [
    { k: 'Số lệnh', v: String(d.trades), c: INK },
    { k: 'Lệnh thắng', v: d.wins + ' (' + trader.wr + '%)', c: POS },
    { k: 'Lệnh thua', v: d.losses + ' (' + (100 - trader.wr) + '%)', c: NEG },
    { k: 'Lệnh lãi lớn nhất', v: sMoney(d.best), c: POS },
    { k: 'Lệnh lỗ lớn nhất', v: sMoney(d.worst), c: NEG },
    { k: 'Lãi gộp', v: money(d.grossWin), c: POS },
    { k: 'Lỗ gộp', v: money(-d.grossLoss), c: NEG },
    { k: 'Thắng liên tiếp nhiều nhất', v: String(d.cWins), c: INK },
    { k: 'Thua liên tiếp nhiều nhất', v: String(d.cLosses), c: INK },
    { k: 'Sharpe ratio', v: d.sharpe.toFixed(2), c: INK },
    { k: 'Mật độ giao dịch', v: d.perWeek + ' lệnh/tuần', c: INK },
    { k: 'Thời gian giữ lệnh TB', v: trader.tags.includes('Swing') ? '2 ngày 4 giờ' : '5 giờ', c: INK },
  ];

  const statR = [
    { k: 'Recovery factor', v: d.recovery.toFixed(2), c: INK },
    { k: 'Lệnh Long', v: Math.round((d.trades * trader.long) / 100) + ' (' + trader.long + '%)', c: INK },
    { k: 'Lệnh Short', v: d.trades - Math.round((d.trades * trader.long) / 100) + ' (' + (100 - trader.long) + '%)', c: INK },
    { k: 'Profit factor', v: trader.pf.toFixed(2), c: trader.pf >= 1 ? POS : NEG },
    { k: 'Expected payoff', v: sMoney(d.exp), c: d.exp >= 0 ? POS : NEG },
    { k: 'Lãi trung bình', v: sMoney(d.avgWin), c: POS },
    { k: 'Lỗ trung bình', v: sMoney(-d.avgLoss), c: NEG },
    { k: 'Risk / reward', v: '1 : ' + d.rr.toFixed(2), c: INK },
    { k: 'Rủi ro trung bình / lệnh', v: d.avgRisk.toFixed(2) + '%', c: d.avgRisk > 1 ? NEG : INK },
    { k: 'Tăng trưởng tháng gần nhất', v: sPct(mv[11]), c: mv[11] >= 0 ? POS : NEG },
    { k: 'Tháng dương', v: winMonths + '/12', c: POS },
    { k: 'Mức sử dụng vốn cao nhất', v: trader.load + '%', c: trader.load > 80 ? NEG : INK },
  ];

  const wlCells = [
    { k: 'Lãi trung bình', v: sMoney(d.avgWin), c: POS },
    { k: 'Lỗ trung bình', v: sMoney(-d.avgLoss), c: NEG },
    { k: 'Lệnh lãi lớn nhất', v: sMoney(d.best), c: POS },
    { k: 'Lệnh lỗ lớn nhất', v: sMoney(d.worst), c: NEG },
    { k: 'Expected payoff', v: sMoney(d.exp), c: d.exp >= 0 ? POS : NEG },
    { k: 'Profit factor', v: trader.pf.toFixed(2), c: trader.pf >= 1 ? POS : NEG },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* 30+ Trading Stats in 2 Columns */}
      <div className="card-box p-4 md:p-5">
        <div className="text-sm font-bold text-gray-900 mb-4">Thống kê giao dịch</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
          <div>
            {statL.map((r) => (
              <div
                key={r.k}
                className="flex justify-between gap-3 py-2 border-b border-dotted border-[#E7EBF0]"
              >
                <span className="text-xs text-[#5C6779]">{r.k}</span>
                <span style={{ color: r.c }} className="font-mono text-xs font-semibold text-right">
                  {r.v}
                </span>
              </div>
            ))}
          </div>
          <div>
            {statR.map((r) => (
              <div
                key={r.k}
                className="flex justify-between gap-3 py-2 border-b border-dotted border-[#E7EBF0]"
              >
                <span className="text-xs text-[#5C6779]">{r.k}</span>
                <span style={{ color: r.c }} className="font-mono text-xs font-semibold text-right">
                  {r.v}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Symbol Breakdown */}
      <div className="card-box p-4 md:p-5">
        <div className="text-sm font-bold text-gray-900 mb-0.5">Phân bố theo symbol</div>
        <div className="text-xs text-[#8B95A3] mb-5">
          Số lệnh, chiều giao dịch và lợi nhuận gộp trên từng symbol
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Deals */}
          <div>
            <div className="text-xs font-semibold text-[#5C6779] mb-3">Số lệnh</div>
            {distSym.map((dItem) => (
              <div key={dItem.sym} className="flex items-center gap-2.5 mb-2.5">
                <div className="font-mono text-xs w-16 flex-shrink-0 text-[#5C6779]">{dItem.sym}</div>
                <div className="font-mono text-xs w-8 text-right flex-shrink-0 text-gray-900">
                  {dItem.deals}
                </div>
                <div className="flex-1 h-3.5 bg-[#F4F6F9] !rounded">
                  <div
                    style={{ width: dItem.dealW }}
                    className="h-full bg-[#CDE9EA] border border-[#7FCFD0] !rounded"
                  ></div>
                </div>
              </div>
            ))}
          </div>

          {/* Buy vs Sell */}
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-[#5C6779] mb-3">
              <div className="w-16 flex-shrink-0"></div>
              <div className="flex-1 text-right">Sell</div>
              <div className="w-px"></div>
              <div className="flex-1">Buy</div>
            </div>
            {distSym.map((dItem) => (
              <div key={dItem.sym} className="flex items-center gap-2 mb-2.5">
                <div className="font-mono text-xs w-16 flex-shrink-0 text-[#5C6779]">{dItem.sym}</div>
                <div className="flex-1 flex justify-end h-3.5 bg-[#F4F6F9] !rounded-l">
                  <div
                    style={{ width: dItem.sellW }}
                    className="h-full bg-[#FADED9] border border-[#E8A79E]"
                  ></div>
                </div>
                <div className="w-px h-4.5 bg-[#C6CDD6]"></div>
                <div className="flex-1 h-3.5 bg-[#F4F6F9] !rounded-r">
                  <div
                    style={{ width: dItem.buyW }}
                    className="h-full bg-[#D8EFDF] border border-[#93CFA6]"
                  ></div>
                </div>
              </div>
            ))}
          </div>

          {/* Gross Profit */}
          <div>
            <div className="text-xs font-semibold text-[#5C6779] mb-3">Lợi nhuận gộp (USD)</div>
            {distSym.map((dItem) => (
              <div key={dItem.sym} className="flex items-center gap-2.5 mb-2.5">
                <div className="font-mono text-xs w-16 flex-shrink-0 text-[#5C6779]">{dItem.sym}</div>
                <div
                  style={{ color: dItem.gpC }}
                  className="font-mono text-xs w-14 text-right flex-shrink-0 font-semibold"
                >
                  {dItem.gp}
                </div>
                <div className="flex-1 h-3.5 bg-[#F4F6F9] !rounded">
                  <div
                    style={{
                      width: dItem.gpW,
                      background: dItem.gpBg,
                      borderColor: dItem.gpBd,
                    }}
                    className="h-full border !rounded"
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 3 Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
        {/* Win / Loss Analysis */}
        <div className="card-box p-4 md:p-5">
          <div className="text-sm font-bold text-gray-900 mb-4">Phân tích thắng / thua</div>
          <div className="flex h-8 !rounded-lg overflow-hidden mb-2">
            <div
              style={{ width: trader.wr + '%' }}
              className="bg-[#1FA463] flex items-center justify-center text-white font-mono text-xs font-semibold"
            >
              {trader.wr}%
            </div>
            <div className="flex-1 bg-[#E0524D] flex items-center justify-center text-white font-mono text-xs font-semibold">
              {100 - trader.wr}%
            </div>
          </div>
          <div className="flex justify-between text-xs text-[#8B95A3] mb-4">
            <span>{d.wins} lệnh thắng</span>
            <span>{d.losses} lệnh thua</span>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {wlCells.map((c) => (
              <div key={c.k} className="bg-[#F7F9FB] !rounded-lg p-2.5">
                <div className="text-[11px] text-[#5C6779] mb-1">{c.k}</div>
                <div style={{ color: c.c }} className="font-mono text-xs font-semibold">
                  {c.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Profit Distribution Histogram */}
        <div className="card-box p-4 md:p-5">
          <div className="text-sm font-bold text-gray-900 mb-0.5">Phân bố lợi nhuận mỗi lệnh</div>
          <div className="text-xs text-[#8B95A3] mb-4">Số lệnh theo khoảng lãi/lỗ tính trên % vốn</div>
          <div className="flex items-end gap-1.5 h-[150px]">
            {dist.map((dItem) => (
              <div key={dItem.label} className="flex-1 flex flex-col justify-end items-center gap-1.5">
                <div className="font-mono text-[10px] text-[#8B95A3]">{dItem.n}</div>
                <div
                  style={{ height: dItem.h, background: dItem.c }}
                  className="w-full !rounded-t-sm"
                ></div>
                <div className="font-mono text-[9px] text-[#A6AFBC] text-center whitespace-nowrap">
                  {dItem.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trading Activity Heatmap */}
        <div className="card-box p-4 md:p-5">
          <div className="text-sm font-bold text-gray-900 mb-0.5">Mật độ giao dịch</div>
          <div className="text-xs text-[#8B95A3] mb-4">Số lệnh mỗi tuần trong 24 tuần gần nhất</div>
          <div className="flex items-end gap-1 h-[110px]">
            {activity.map((a, idx) => (
              <div
                key={idx}
                style={{ height: a.h, background: a.c }}
                className="flex-1 !rounded-t-sm"
              ></div>
            ))}
          </div>
          <div className="flex justify-between mt-2 font-mono text-[10.5px] text-[#A6AFBC]">
            <span>24 tuần trước</span>
            <span>Tuần này</span>
          </div>
        </div>
      </div>
    </div>
  );
};
